import { cn } from "@/lib/utils";
import { ChatMessage as ApiChatMessage } from "@/api/generated/client/models";
import { Bot, User } from "lucide-react";
import { format } from "date-fns";

interface ChatMessageProps {
    message: ApiChatMessage;
    isLatest?: boolean;
}

// Simple markdown renderer for basic formatting
function SimpleMarkdown({ content }: { content: string }) {
    // Split by code blocks first
    const parts = content.split(/(```[\s\S]*?```|`[^`]+`)/);
    
    return (
        <div className="space-y-2" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
            {parts.map((part, idx) => {
                // Code block
                if (part.startsWith('```') && part.endsWith('```')) {
                    const code = part.slice(3, -3).trim();
                    return (
                        <pre key={idx} className="bg-muted p-3 rounded-md border overflow-x-auto max-w-full" style={{ wordBreak: 'break-word' }}>
                            <code className="text-xs font-mono break-words whitespace-pre-wrap">{code}</code>
                        </pre>
                    );
                }
                // Inline code
                if (part.startsWith('`') && part.endsWith('`')) {
                    return (
                        <code key={idx} className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono break-words inline-block max-w-full" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                            {part.slice(1, -1)}
                        </code>
                    );
                }
                // Regular text with basic markdown
                return (
                    <div key={idx} className="whitespace-pre-wrap leading-relaxed break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                        {part.split('\n').map((line, lineIdx) => {
                            // Bold
                            const boldFormatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                            // Italic
                            const italicFormatted = boldFormatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
                            
                            return (
                                <p 
                                    key={lineIdx} 
                                    dangerouslySetInnerHTML={{ __html: italicFormatted }}
                                    className="my-1 break-words"
                                    style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                />
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}

export function ChatMessage({ message, isLatest = false }: ChatMessageProps) {
    const isUser = message.role === 'user';
    const isAssistant = message.role === 'assistant';
    const isSystem = message.role === 'system';

    return (
        <div
            className={cn(
                "group flex gap-3 px-4 py-4 transition-colors w-full max-w-full min-w-0",
                isLatest && "animate-in fade-in slide-in-from-bottom-2 duration-300",
                isUser && "bg-background",
                isAssistant && "bg-muted/30",
                isSystem && "bg-blue-50/50 dark:bg-blue-950/20"
            )}
        >
            {/* Avatar */}
            <div className="flex-shrink-0">
                <div
                    className={cn(
                        "flex h-8 w-8 items-center justify-center rounded-full",
                        isUser && "bg-primary text-primary-foreground",
                        isAssistant && "bg-gradient-to-br from-purple-500 to-pink-500 text-white",
                        isSystem && "bg-blue-500 text-white"
                    )}
                >
                    {isUser && <User className="h-4 w-4" />}
                    {isAssistant && <Bot className="h-4 w-4" />}
                    {isSystem && <span className="text-xs font-bold">S</span>}
                </div>
            </div>

            {/* Message Content */}
            <div className="flex-1 min-w-0 space-y-1 overflow-hidden">
                <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold">
                        {isUser && "You"}
                        {isAssistant && "TurboAI"}
                        {isSystem && "System"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                        {format(new Date(message.createdAt), 'h:mm a')}
                    </span>
                </div>

                <div className="text-sm text-foreground break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {isAssistant ? (
                        <SimpleMarkdown content={message.content} />
                    ) : (
                        <p className="whitespace-pre-wrap leading-relaxed break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{message.content}</p>
                    )}
                </div>
            </div>
        </div>
    );
}

